import React, {
  useEffect, useMemo, useState, useContext, useRef,
  useCallback, Suspense, startTransition, useDeferredValue
} from "react";
import {
  Box, Stack, Paper, Typography, Chip, TextField, InputAdornment,
  MenuItem, Select, FormControl, InputLabel, Button, Divider,
  Drawer, List, ListItem, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Skeleton, IconButton, Tooltip
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import { useEmpresas } from "../../../hooks/useEmpresas";
import { UserContext } from "../../../context/provider/UserProvider";
import { formatDate, notify } from "../../../utils/utils";
import { Fetch } from "../../../services/api";

// Lazy imports
const ProjectCard = React.lazy(() => import("../../components/kanban/ProjectCard"));
const ProjectDetailDialog = React.lazy(() => import("../../components/kanban/ProjectDetailDialog"));

/* ===================== Constantes API ===================== */
const XPROJECT_API = "https://xprojectwebapi.abexacloud.com/api/Proyect";

/* ===================== Helpers fecha ===================== */
function ddmmToISO(ddmmyyyy) {
  const s = String(ddmmyyyy || "");
  const [dd, mm, yyyy] = s.split("/");
  if (!dd || !mm || !yyyy) return "";
  return `${yyyy.padStart(4,"0")}-${mm.padStart(2,"0")}-${dd.padStart(2,"0")}`;
}
function isoToDDMM(iso) {
  const s = String(iso || "");
  const [yyyy, mm, dd] = s.split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd.padStart(2,"0")}/${mm.padStart(2,"0")}/${yyyy.padStart(4,"0")}`;
}
function todayDDMM() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth()+1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}
// parser flexible para ordenar por fechaRegistro
function tsFromAny(v) {
  if (!v) return 0;
  const s = String(v).trim();
  // dd/mm/yyyy (con o sin hora)
  const m = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?\s*$/.exec(s);
  if (m) {
    const dd = Number(m[1]), mm = Number(m[2]), yyyy = Number(m[3]);
    const hh = Number(m[4] || 0), mi = Number(m[5] || 0), ss = Number(m[6] || 0);
    const d = new Date(yyyy, mm - 1, dd, hh, mi, ss);
    const t = d.getTime();
    return Number.isFinite(t) ? t : 0;
  }
  const d2 = new Date(s);
  const t2 = d2.getTime();
  return Number.isFinite(t2) ? t2 : 0;
}

/* ===================== Icono Search (claro) ===================== */
const SearchSvgIcon = ({ size = 18 }) => (
  <Box component="span" sx={{ display:"inline-flex", alignItems:"center", justifyContent:"center", mr: 1 }}>
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" role="img">
      <path fill="#dfe7ff" d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 0 0 1.57-4.23C15.99 6.01 13.48 3.5 10.5 3.5S5.01 6.01 5.01 9s2.51 5.5 5.49 5.5c1.61 0 3.06-.66 4.12-1.73l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14Zm-5 0C8.01 14 6 11.99 6 9.5S8.01 5 10.5 5 15 7.01 15 9.5 12.99 14 10.5 14Z"/>
    </svg>
  </Box>
);

/* ===================== Upload evidencias (finalizar) ===================== */
const MINIWEB_UPLOAD_BASE = "https://miniowebapi.abexacloud.com/api/Archivo";
function tsName() {
  const d = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return `${pad(d.getDate())}_${pad(d.getMonth()+1)}_${d.getFullYear()}_${pad(d.getHours())}_${pad(d.getMinutes())}_${pad(d.getSeconds())}`;
}
function extOf(fileNameOrType = "") {
  const lower = String(fileNameOrType).toLowerCase();
  const byName = lower.split(".").pop();
  if (byName && byName !== lower) return byName;
  if (lower.includes("png")) return "png";
  if (lower.includes("jpg") || lower.includes("jpeg")) return "jpg";
  if (lower.includes("webp")) return "webp";
  if (lower.includes("gif")) return "gif";
  return "png";
}
async function uploadImageToMinio(file, baseName) {
  const name = baseName || tsName();
  const url = `${MINIWEB_UPLOAD_BASE}/subirArchivo?NombreCarpeta=xbest&NombreSubCarpeta=x_proyect/&nombreArchivo=${encodeURIComponent(name)}`;
  const fd = new FormData();
  const extension = extOf(file.name || file.type);
  const serverFileName = `${name}.${extension}`;
  fd.append("archivo", file, serverFileName);
  const resp = await fetch(url, { method: "POST", body: fd });
  if (!resp.ok) throw new Error(`Upload error: ${resp.status}`);
  const json = await resp.json();
  if (!json?.isValid) throw new Error(json?.exceptions?.[0]?.description || "Error subiendo archivo");
  const verUrl = `${MINIWEB_UPLOAD_BASE}/verImagen?NombreCarpeta=xbest&NombreImagen=${encodeURIComponent("x_proyect/" + name)}`;
  return { urlArchivo: verUrl, nomArchivo: name, extensionArchivo: extension, codArchivoTipo: 2 };
}

/* ===================== Drop/Paste Zone ===================== */
const DropPasteZone = React.memo(function DropPasteZone({ files, setFiles }) {
  const ref = useRef(null);
  const onDrop = useCallback((ev) => {
    ev.preventDefault(); ev.stopPropagation();
    const list = [];
    if (ev.dataTransfer?.items) {
      for (const item of Array.from(ev.dataTransfer.items)) {
        if (item.kind === "file") {
          const f = item.getAsFile();
          if (f) list.push(f);
        }
      }
    } else if (ev.dataTransfer?.files) {
      for (const f of Array.from(ev.dataTransfer.files)) list.push(f);
    }
    if (list.length) setFiles((prev)=> [...prev, ...list]);
  }, [setFiles]);
  const onPaste = useCallback((ev) => {
    if (!ev.clipboardData) return;
    const items = ev.clipboardData.items || [];
    const list = [];
    for (const it of Array.from(items)) {
      if (it.type.indexOf("image") !== -1) {
        const f = it.getAsFile();
        if (f) list.push(f);
      }
    }
    if (list.length) setFiles((prev)=> [...prev, ...list]);
  }, [setFiles]);
  const prevent = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
  const onPick = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.multiple = true;
    input.onchange = () => {
      const list = Array.from(input.files || []);
      if (list.length) setFiles((prev)=> [...prev, ...list]);
    };
    input.click();
  };
  return (
    <Box
      ref={ref}
      onDragEnter={prevent} onDragOver={prevent} onDragLeave={prevent} onDrop={onDrop}
      onPaste={onPaste}
      sx={{
        mt: 1, p: 2, borderRadius: 2, border: "1.5px dashed #3a4457",
        bgcolor: "#0f141c", color: "#cfd8e3", textAlign: "center", cursor: "pointer",
        "&:hover": { borderColor: "#6aa3ff", bgcolor: "#121a25" }
      }}
      onClick={onPick}
      title="Click para elegir, arrastra o pega una captura"
    >
      Arrastra o pega captura aquí, o haz click para seleccionar imágenes
      <Stack direction="row" spacing={1} mt={1} justifyContent="center" flexWrap="wrap">
        {files.map((f, i)=> (
          <Box key={i} sx={{ p: .5, border:"1px solid #2c3442", borderRadius: 1, bgcolor:"#0b0f14" }}>
            <Typography variant="caption">{f.name || `(imagen ${i+1})`}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
});

/* ===================== Debounce & chunks ===================== */
function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}
function useInfiniteChunks(list, initial = 40, step = 60) {
  const [limit, setLimit] = useState(initial);
  const sentinelRef = useRef(null);
  const reset = useCallback(() => setLimit(initial), [initial]);
  useEffect(() => { reset(); }, [list, reset]);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          startTransition(() => setLimit((v) => Math.min(v + step, list.length)));
        }
      }
    }, { root: null, rootMargin: "200px 0px", threshold: 0.01 });
    io.observe(el);
    return () => io.disconnect();
  }, [list.length, step]);
  return { items: list.slice(0, limit), sentinelRef, limit };
}

/* ===================== Footer de participantes para cada card ===================== */
const ParticipantsFooter = React.memo(function ParticipantsFooter({ participantes }) {
  const parts = useMemo(() => {
    const s = String(participantes || "").trim();
    if (!s) return [];
    return s.split(",").map(x => x.trim()).filter(Boolean);
  }, [participantes]);

  if (!parts.length) return null;

  const max = 5;
  const show = parts.slice(0, max);
  const extra = parts.length - show.length;

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75 }}>
      {show.map((mail, i) => (
        <Chip
          key={`${mail}-${i}`}
          size="small"
          label={mail}
          sx={{
            height: 22,
            bgcolor:"#0f1622",
            color:"#cfe3ff",
            border:"1px solid #2b3a52",
            "& .MuiChip-label": { px: 0.75, fontSize: 11, fontWeight: 700 }
          }}
        />
      ))}
      {extra > 0 && (
        <Chip
          size="small"
          label={`+${extra}`}
          sx={{ height: 22, bgcolor:"#0f1622", color:"#cfe3ff", border:"1px solid #2b3a52" }}
        />
      )}
    </Stack>
  );
});

/* ===================== Card Wrapper (ProjectCard + participantes) ===================== */
/* ===================== Card Wrapper ===================== */
const CardWrapper = React.memo(function CardWrapper({ p, onOpenSeguimientos, onOpenDetail }) {
  return (
    <Suspense
      fallback={
        <Paper sx={{ p:1.5, mb:1.25, borderRadius:3, bgcolor:"#0e1217", border:"1px solid #232a36" }}>
          <Skeleton variant="text" height={22} sx={{ bgcolor:"#1a2030" }}/>
          <Skeleton variant="text" height={18} width="80%" sx={{ bgcolor:"#1a2030" }}/>
          <Skeleton variant="rectangular" height={16} width="30%" sx={{ mt:1, borderRadius:1, bgcolor:"#1a2030" }}/>
        </Paper>
      }
    >
      <ProjectCard p={p} onOpenSeguimientos={onOpenSeguimientos} onOpenDetail={onOpenDetail} />
    </Suspense>
  );
});


/* ===================== Toggle de orden ===================== */
const SortIconToggle = React.memo(function SortIconToggle({ order, onToggle }) {
  const isDesc = order === "desc";
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <CalendarTodayIcon fontSize="small" sx={{ color:"#9aa6b8", opacity:.9 }} />
      <Tooltip title={isDesc ? "Nuevos primero" : "Antiguos primero"}>
        <IconButton
          size="small"
          onClick={onToggle}
          sx={{
            ml:0.25,
            color:"#cfd8e3",
            border:"1px solid #3a4457",
            borderRadius: 1.5,
            p:0.25,
            "&:hover": { color:"#ffffff", borderColor:"#6aa3ff", bgcolor:"#111826" }
          }}
          aria-label={isDesc ? "Nuevos primero" : "Antiguos primero"}
        >
          {isDesc ? <ArrowDownwardIcon fontSize="inherit" /> : <ArrowUpwardIcon fontSize="inherit" />}
        </IconButton>
      </Tooltip>
    </Stack>
  );
});

/* ===================== Componente ===================== */
export const Proyectosv2 = () => {
  const { stateUser } = useContext(UserContext);
  const { empresas } = useEmpresas();

  /* ===== Fechas ===== */
  const [startDateISO, setStartDateISO] = useState(ddmmToISO("01/09/2025")); // inicial
  const endDate = formatDate(new Date()); // dd/mm/yyyy
  const [openDateDlg, setOpenDateDlg] = useState(false);
  const startDateDDMM = useMemo(() => isoToDDMM(startDateISO), [startDateISO]);

  /* ===== Filtros UI ===== */
  const [empresa, setEmpresa] = useState("all");
  const [tipoReq, setTipoReq] = useState(true);
  const [tipoProy, setTipoProy] = useState(true);
  const [priAlta, setPriAlta] = useState(true);
  const [priMedia, setPriMedia] = useState(true);
  const [priBaja, setPriBaja] = useState(true);
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 250);
  const deferredQ = useDeferredValue(debouncedQ);

  /* ===== Tracing ===== */
  const [usuariosArea, setUsuariosArea] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [proyectoSel, setProyectoSel] = useState(null);
  const [tracing, setTracing] = useState([]);
  const [savingSeg, setSavingSeg] = useState(false);
  const [loadingTracing, setLoadingTracing] = useState(false);

  /* ===== Detalle ===== */
  const [openDetail, setOpenDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);

  /* ===== Board ===== */
  const [board, setBoard] = useState({ pendiente: [], asignado: [], enproceso: [], finalizado: [] });
  const [loading, setLoading] = useState(false);

  /* ===== Orden por columna ===== */
  const [sortOrders, setSortOrders] = useState({
    pendiente: "desc",
    asignado: "desc",
    enproceso: "desc",
  });
  const toggleSort = useCallback((col) => {
    setSortOrders((prev) => ({ ...prev, [col]: prev[col] === "desc" ? "asc" : "desc" }));
  }, []);

  /* ===== Cierre ===== */
  const [closeDlgOpen, setCloseDlgOpen] = useState(false);
  const [closingTicket, setClosingTicket] = useState(null);
  const [closeReason, setCloseReason] = useState("");
  const [closeFiles, setCloseFiles] = useState([]);
  const [closing, setClosing] = useState(false);
  const [revertMove, setRevertMove] = useState(null);

  /* ===== UI: Finalizados (ver todo / solo 10) ===== */
  const [showAllFinalizados, setShowAllFinalizados] = useState(false);

  /* ===== Usuarios (codArea=1) ===== */
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resp = await Fetch({ url: `${process.env.REACT_APP_XPROJECT_API || XPROJECT_API}/User?codArea=1` });
        if (!active) return;
        if (resp?.isValid) setUsuariosArea(resp.content || []);
      } catch { setUsuariosArea([]); }
    })();
    return () => { active = false; };
  }, []);

  /* ===== Loader Kanban (nuevo endpoint) ===== */
  const loadKanban = useCallback(async () => {
    const codUsuario = Number(stateUser?.codUsuario || 61); // fallback 61 (ejemplo)
    const fechaInicio = isoToDDMM(startDateISO) || "01/01/2020";
    const fechaFin = endDate; // dd/mm/yyyy
    const qs = new URLSearchParams({
      CodUsuario: String(codUsuario),
      FechaInicio: fechaInicio,
      FechaFin: fechaFin
    }).toString();
    const url = `${XPROJECT_API}/ticketForKanban?${qs}`;
    setLoading(true);
    try {
      const resp = await Fetch({ url });
      if (!resp?.isValid || !resp?.content) throw new Error(resp?.exceptions?.[0]?.description || "Error al listar");
      // Normalizar enProceso -> enproceso
      const content = resp.content || {};
      const normalized = {
        pendiente: Array.isArray(content.pendiente) ? content.pendiente : [],
        asignado: Array.isArray(content.asignado) ? content.asignado : [],
        enproceso: Array.isArray(content.enProceso) ? content.enProceso : [],
        finalizado: Array.isArray(content.finalizado) ? content.finalizado : []
      };
      setBoard(normalized);
    } catch (e) {
      notify(String(e?.message || e || "Error de red"), "error");
      setBoard({ pendiente: [], asignado: [], enproceso: [], finalizado: [] });
    } finally {
      setLoading(false);
    }
  }, [stateUser, startDateISO, endDate]);

  /* ===== Primera carga y recargas por fecha ===== */
  useEffect(() => {
    loadKanban();
  }, [loadKanban]);

  const handleReload = useCallback(() => {
    startTransition(() => { loadKanban(); });
  }, [loadKanban]);

  /* ====== Indexación + Filtros ====== */
  // aplanar para filtrar
  const allItems = useMemo(() => {
    const withCol = (arr, col) => (arr || []).map(p => ({
      ...p,
      _colKey: col,
      _lcTitle: String(p.nomTicket || "").toLowerCase(),
      _lcEmpresa: String(p.nomEmpresa || "").toLowerCase(),
      _lcResp: String(p.nomUsuarioResponsable || "").toLowerCase(),
      _regTs: tsFromAny(p.fechaRegistro)
    }));
    return [
      ...withCol(board.pendiente, "pendiente"),
      ...withCol(board.asignado, "asignado"),
      ...withCol(board.enproceso, "enproceso"),
      ...withCol(board.finalizado, "finalizado"),
    ];
  }, [board]);

  const filterPredicate = useCallback((p, searchQuery) => {
    if (empresa !== "all") {
      const val = String(p.nomEmpresa || p.codEmpresa);
      if (String(val) !== String(empresa)) return false;
    }
    if (!(tipoReq && tipoProy)) {
      const t = String(p.nomTicketTipo || "").toLowerCase();
      if (tipoReq && !tipoProy) { if (t !== "requerimiento") return false; }
      if (!tipoReq && tipoProy) { if (t !== "proyecto") return false; }
    }
    if (!(priAlta && priMedia && priBaja)) {
      const pr = String(p.nomPrioridad || "").toUpperCase();
      const okAlta  = priAlta  && pr.includes("ALTA");
      const okMedia = priMedia && pr.includes("MEDIA");
      const okBaja  = priBaja  && pr.includes("BAJA");
      if (!(okAlta || okMedia || okBaja)) return false;
    }
    const raw = (searchQuery || "").trim().toLowerCase();
    if (raw) {
      const digits = raw.replace(/[^\d]/g, "");
      const byCode    = digits ? String(p.codTicket || "").includes(digits) : false;
      const byTitle   = p._lcTitle.includes(raw);
      const byEmpresa = p._lcEmpresa.includes(raw);
      const byResp    = p._lcResp.includes(raw);
      if (!(byCode || byTitle || byEmpresa || byResp)) return false;
    }
    return true;
  }, [empresa, tipoReq, tipoProy, priAlta, priMedia, priBaja]);

  const filtered = useMemo(() => {
    if (!allItems.length) return [];
    return allItems.filter((p) => filterPredicate(p, deferredQ));
  }, [allItems, filterPredicate, deferredQ]);

  // reagrupar y ordenar columnas visibles
  const { grouped, totalFinalizados, showingFinalizadosCount } = useMemo(() => {
    const pend = [];
    const asig = [];
    const proc = [];
    const finAll = [];
    for (const p of filtered) {
      if (p._colKey === "pendiente") pend.push(p);
      else if (p._colKey === "asignado") asig.push(p);
      else if (p._colKey === "enproceso") proc.push(p);
      else if (p._colKey === "finalizado") finAll.push(p);
    }
    const sortByReg = (arr, order) => {
      arr.sort((a, b) => {
        const ta = a._regTs || 0;
        const tb = b._regTs || 0;
        if (ta !== tb) return order === "asc" ? ta - tb : tb - ta;
        const ca = Number(a.codTicket || 0);
        const cb = Number(b.codTicket || 0);
        return order === "asc" ? ca - cb : cb - ca;
      });
    };
    sortByReg(pend, sortOrders.pendiente);
    sortByReg(asig, sortOrders.asignado);
    sortByReg(proc, sortOrders.enproceso);

    // finalizados: ya vienen como lista; usamos fechaRegistro para consistencia desc
    finAll.sort((a, b) => (b._regTs || 0) - (a._regTs || 0));

    const total = finAll.length;
    const slice = showAllFinalizados ? finAll : finAll.slice(0, 10);
    return { grouped: { pendiente: pend, asignado: asig, enproceso: proc, finalizado: slice }, totalFinalizados: total, showingFinalizadosCount: slice.length };
  }, [filtered, showAllFinalizados, sortOrders]);

  // Chunks por columna (infinite scroll)
  const pendChunks = useInfiniteChunks(grouped.pendiente, 40, 60);
  const asigChunks = useInfiniteChunks(grouped.asignado, 40, 60);
  const procChunks = useInfiniteChunks(grouped.enproceso, 40, 60);
  const finChunks  = useInfiniteChunks(grouped.finalizado, 40, 60);

  /* ====== Tracing ====== */
  const fetchTracing = useCallback(async (codTicket) => {
    setLoadingTracing(true);
    try {
      const url = `${process.env.REACT_APP_XPROJECT_API || XPROJECT_API}/Tracing?` + new URLSearchParams({ CodTicket: String(codTicket) });
      const resp = await Fetch({ url });
      setTracing(resp?.isValid && Array.isArray(resp.content) ? resp.content : []);
    } catch { setTracing([]); }
    finally { setLoadingTracing(false); }
  }, []);

  const openDrawerSeguimientos = useCallback(async (p) => {
    setProyectoSel(p);
    setNuevoSeg("");
    setDrawerOpen(true);
    await fetchTracing(p.codTicket);
  }, [fetchTracing]);

  const [nuevoSeg, setNuevoSeg] = useState("");
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setProyectoSel(null);
    setNuevoSeg("");
    setTracing([]);
  }, []);

  const handleAddSeguimiento = useCallback(async () => {
    const texto = (nuevoSeg || "").trim();
    if (!proyectoSel || !texto) return;
    try {
      setSavingSeg(true);
      const body = { codUsuarioAccion: Number(stateUser?.codUsuario || 1), codTicket: Number(proyectoSel.codTicket), descripcion: texto };
      const resp = await Fetch({
        url: `${process.env.REACT_APP_XPROJECT_API || XPROJECT_API}/Tracing`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!resp?.isValid) {
        const msg = resp?.exceptions?.[0]?.description || resp?.content || "No se pudo registrar el seguimiento";
        notify(msg, "error"); setSavingSeg(false); return;
      }
      notify(resp?.content || "Se registró correctamente", "success");
      setNuevoSeg("");
      await fetchTracing(proyectoSel.codTicket);
    } catch { notify("Error de red al registrar seguimiento", "error"); }
    finally { setSavingSeg(false); }
  }, [nuevoSeg, proyectoSel, stateUser, fetchTracing]);

  /* ===== Detalle: usa el endpoint detailForTicket y lo inyecta en data._detail ===== */
  const handleOpenDetail = useCallback(async (p) => {
    setDetailData(null);
    setOpenDetail(true);
    try {
      const url = `${XPROJECT_API}/detailForTicket?` + new URLSearchParams({ CodTicket: String(p.codTicket) });
      const resp = await Fetch({ url });
      const detail = resp?.isValid ? (resp.content || {}) : {};
      setDetailData({ ...p, _detail: detail });
    } catch {
      setDetailData(p);
    }
  }, []);
  const handleCloseDetail = useCallback(() => { setOpenDetail(false); setDetailData(null); }, []);

  /* ============ Drag & Drop (nota: actualizar estado si tienes endpoint) ============ */
  const onDragEnd = useCallback(async (result) => {
    const { destination, source } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // Movimiento optimista en UI
    const getArr = (id) => {
      switch (id) {
        case "pendiente": return [...grouped.pendiente];
        case "asignado": return [...grouped.asignado];
        case "enproceso": return [...grouped.enproceso];
        case "finalizado": return [...grouped.finalizado];
        default: return [];
      }
    };
    let src = getArr(source.droppableId);
    let dst = getArr(destination.droppableId);
    const moved = src[source.index];
    src.splice(source.index, 1);
    dst.splice(destination.index, 0, moved);

    // si lo llevan a finalizado, abre diálogo de cierre
    if (destination.droppableId === "finalizado" && source.droppableId !== "finalizado") {
      setRevertMove({ source, destination });
      setClosingTicket(moved || null);
      setCloseReason("");
      setCloseFiles([]);
      setCloseDlgOpen(true);
      return;
    }

    // En este ejemplo no hay endpoint de "cambiar estado", recargamos después
    startTransition(() => loadKanban());
  }, [grouped, loadKanban]);

  const doRevertMove = useCallback(() => {
    // sólo recarga, más simple y consistente
    startTransition(() => loadKanban());
  }, [loadKanban]);

  const handleCancelClose = useCallback(() => {
    doRevertMove();
    setCloseDlgOpen(false);
    setClosingTicket(null);
    setCloseFiles([]);
    setCloseReason("");
    setRevertMove(null);
  }, [doRevertMove]);

  const handleConfirmClose = useCallback(async () => {
    if (!closingTicket) return;
    if (!closeReason.trim()) { notify("Escribe un motivo de cierre", "warning"); return; }
    try {
      setClosing(true);
      const parametrosImagen = [];
      for (const f of closeFiles) parametrosImagen.push(await uploadImageToMinio(f, tsName()));
      const codUsuarioAccion = Number(stateUser?.codUsuario || 1);
      const body = {
        codTicket: Number(closingTicket.codTicket),
        codUsuarioAccion,
        motivo: closeReason.trim(),
        parametrosImagen,
        parametrosArchivo: []
      };
      const resp = await fetch(`${XPROJECT_API}/finalizar`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      if (!json?.isValid) throw new Error(json?.exceptions?.[0]?.description || json?.content || "No se pudo finalizar el ticket");
      notify(json?.content || "Se finalizó el ticket correctamente", "success");
      setCloseDlgOpen(false); setClosingTicket(null); setCloseFiles([]); setCloseReason(""); setRevertMove(null);
      await loadKanban();
    } catch (err) {
      console.error(err); notify(String(err?.message || err), "error");
      doRevertMove();
      setCloseDlgOpen(false); setClosingTicket(null); setCloseFiles([]); setCloseReason(""); setRevertMove(null);
    } finally { setClosing(false); }
  }, [closingTicket, closeReason, closeFiles, stateUser, loadKanban, doRevertMove]);

  /* ===== Estilos base ===== */
  const darkPaper = useMemo(() => ({
    bgcolor:"#0b0f14", border:"1px solid #2c3442", color:"#e5e9f0",
    boxShadow:"0 6px 24px rgba(0,0,0,.35)", borderRadius: 5
  }), []);
  const chipToggleSx = useCallback((active, accent = "#3a4457", glow = "rgba(106,163,255,.25)") => ({
    px: 1.25, height: 34, borderRadius: 2, fontWeight: 800, letterSpacing: .2,
    bgcolor: active ? "#141a24" : "transparent",
    color: active ? "#dfe7ff" : "#9aa6b8",
    border: `1.5px solid ${active ? accent : "#2c3442"}`,
    boxShadow: active ? `0 0 0 3px ${glow} inset` : "none",
    "&:hover": { borderColor: "#6aa3ff", bgcolor: active ? "#161e2b" : "#0f141c" }
  }), []);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, minHeight:"90vh", color:"#e5e9f0", bgcolor:"transparent" }}>
      {/* Barra de filtros */}

<Paper
  variant="outlined"
  sx={{
    p: 2, mb: 2, ...darkPaper,
    "& .MuiSvgIcon-root": { color: "#dfe7ff" },
    "& input[type='date']": { color: "#e5e9f0", caretColor: "#e5e9f0" },
    "& input[type='date']::-webkit-calendar-picker-indicator": { filter: "invert(1) opacity(.9)", cursor: "pointer" }
  }}
>
  <Stack direction={{ xs: "column", xl: "row" }} spacing={1.5} alignItems={{ xs: "stretch", xl: "center" }}>
    {/* 1) Fecha */}
    <Chip
      clickable
      label={`Desde: ${startDateDDMM || "—"}`}
      onClick={() => setOpenDateDlg(true)}
      sx={{
        px: 1.25, height: 36, borderRadius: 2, fontWeight: 800, letterSpacing: .2,
        bgcolor: "#141a24", color: "#dfe7ff", border: "1.5px solid #3a4457",
        boxShadow: "0 0 0 3px rgba(106,163,255,.18) inset",
        "&:hover": { borderColor: "#6aa3ff", bgcolor: "#161e2b", cursor: "pointer" }
      }}
    />

    {/* 2) Empresa */}
    <FormControl sx={{ minWidth: 220 }}>
      <InputLabel sx={{ color:"#9aa6b8" }}>Empresa</InputLabel>
      <Select
        label="Empresa"
        value={empresa}
        onChange={(e)=> setEmpresa(e.target.value)}
        sx={{ color:"#e5e9f0", "& fieldset":{ borderColor:"#3a4457" } }}
        MenuProps={{ PaperProps: { sx: { bgcolor:"#0b0f14", color:"#e5e9f0", border:"1px solid #2c3442" } } }}
      >
        <MenuItem value="all">Todas</MenuItem>
        {Array.isArray(empresas) && empresas.map((em)=>(
          <MenuItem key={em.codEmpresa || em.id} value={em.nomEmpresa || em.codEmpresa}>
            {em.nomEmpresa || em.nombre}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    {/* 3) Tipo */}
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" sx={{ color:"#9aa6b8" }}>Tipo:</Typography>
      <Chip label="Requerimiento" clickable onClick={()=> setTipoReq(v=>!v)} sx={chipToggleSx(tipoReq, "#3a4457", "rgba(143,122,255,.22)")} />
      <Chip label="Proyecto" clickable onClick={()=> setTipoProy(v=>!v)} sx={chipToggleSx(tipoProy, "#3a4457", "rgba(143,122,255,.22)")} />
    </Stack>

    {/* 4) Prioridad */}
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" sx={{ color:"#9aa6b8" }}>Prioridad:</Typography>
      <Chip label="ALTA"  clickable onClick={()=> setPriAlta(v=>!v)}  sx={chipToggleSx(priAlta,  "#a33d45", "rgba(255,107,107,.22)")} />
      <Chip label="MEDIA" clickable onClick={()=> setPriMedia(v=>!v)} sx={chipToggleSx(priMedia, "#355e9e", "rgba(106,163,255,.18)")} />
      <Chip label="BAJA"  clickable onClick={()=> setPriBaja(v=>!v)}  sx={chipToggleSx(priBaja,  "#2b8f63", "rgba(103,232,168,.18)")} />
    </Stack>

    {/* 5) Procesar */}
    <Button
      variant="contained"
      onClick={handleReload}
      sx={{ bgcolor:"#6aa3ff", color:"#0b0f14", minWidth: 44, px: 2, "&:hover":{ bgcolor:"#86b6ff" } }}
      title="Procesar"
    >
      {`>`}
    </Button>

    {/* 6) Buscador */}
    <TextField
      placeholder="Buscar por ticket, título, empresa o responsable"
      value={q}
      onChange={(e)=> setQ(e.target.value)}
      InputProps={{ startAdornment: (<InputAdornment position="start"><SearchSvgIcon /></InputAdornment>) }}
      sx={{
        flex: 1,
        input:{ color:"#e5e9f0", caretColor:"#e5e9f0" },
        "& fieldset":{ borderColor:"#3a4457" }, "&:hover fieldset":{ borderColor:"#6aa3ff" }
      }}
    />

    {/* 7) Título (derecha) */}
    <Typography variant="h6" fontWeight={900} sx={{ ml: "auto", whiteSpace:"nowrap", color:"#dfe7ff", textAlign:"right" }}>
      Kanban 
    </Typography>
  </Stack>
</Paper>


      {loading && (
        <LinearProgress sx={{ mb: 2, borderRadius: 2, bgcolor:"#141a24", "& .MuiLinearProgress-bar":{ bgcolor:"#6aa3ff" } }} />
      )}

      {/* Kanban */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Stack direction="row" spacing={2} sx={{ overflowX: "auto", pb: 1 }} divider={<Divider flexItem orientation="vertical" sx={{ borderColor:"#2c3442" }}/>}>
          {[
            { id:"pendiente",  title:"Pendiente",  data: pendChunks.items, total: grouped.pendiente.length,  sentinelRef: pendChunks.sentinelRef,
              headerExtra: (<SortIconToggle order={sortOrders.pendiente} onToggle={()=> toggleSort("pendiente")} />)
            },
            { id:"asignado",   title:"Asignado",   data: asigChunks.items, total: grouped.asignado.length,   sentinelRef: asigChunks.sentinelRef,
              headerExtra: (<SortIconToggle order={sortOrders.asignado} onToggle={()=> toggleSort("asignado")} />)
            },
            { id:"enproceso",  title:"En Proceso", data: procChunks.items, total: grouped.enproceso.length,  sentinelRef: procChunks.sentinelRef,
              headerExtra: (<SortIconToggle order={sortOrders.enproceso} onToggle={()=> toggleSort("enproceso")} />)
            },
            { id:"finalizado", title:"Finalizado", data: finChunks.items,  total: totalFinalizados,          sentinelRef: finChunks.sentinelRef,
              headerExtra: (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip size="small" variant="outlined" label={`Mostrando ${showingFinalizadosCount}${!showAllFinalizados ? " / 10" : ""}`} sx={{ borderColor:"#3a4457", color:"#cfd8e3" }} />
                  <Tooltip title={showAllFinalizados ? "Ver solo últimos 10" : "Ver todos"}>
                    <IconButton size="small" onClick={() => setShowAllFinalizados(v => !v)} sx={{ color:"#cfd8e3", "&:hover": { color:"#ffffff" } }}>
                      {showAllFinalizados ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Stack>
              )
            },
          ].map((col) => (
            <Paper key={col.id} variant="outlined" sx={{
              flex:1, minWidth: 360, maxWidth: "25%", borderRadius:5, p:1,
              bgcolor:"transparent", color:"#e5e9f0", display:"flex", flexDirection:"column",
              height:"calc(100vh - 240px)", boxShadow:"0 6px 24px rgba(0,0,0,.35)",
              transition:"background .2s ease"
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ borderBottom:"1px solid #2c3442", mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={900} sx={{ display:"flex", alignItems:"center", gap:1 }}>
                    {col.title}
                    {col.id === "finalizado" && (
                      <Tooltip title="Más opciones">
                        <MoreHorizIcon fontSize="small" sx={{ opacity:.8 }} />
                      </Tooltip>
                    )}
                  </Typography>
                  <Chip size="small" label={col.total || 0} sx={{ bgcolor:"#111624", color:"#cfd8e3", border:"1px solid #3a4457" }} />
                </Stack>
                {col.headerExtra}
              </Stack>

              <Droppable droppableId={col.id}>
                {(provided)=>(
                  <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ flex:1, overflowY:"auto", pr:.5 }}>
                    {loading && (
                      <Box sx={{ px: 0.5 }}>
                        {[...Array(3)].map((_, i)=>(
                          <Paper key={i} sx={{ p:1.5, mb:1.5, borderRadius:3, bgcolor:"#0e1217", border:"1px solid #232a36" }}>
                            <Skeleton variant="text" height={22} sx={{ bgcolor:"#1a2030" }}/>
                            <Skeleton variant="text" height={18} width="80%" sx={{ bgcolor:"#1a2030" }}/>
                            <Skeleton variant="rectangular" height={16} width="30%" sx={{ mt:1, borderRadius:1, bgcolor:"#1a2030" }}/>
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {!loading && (col.data || []).map((p, idx)=>(
                      <Draggable key={p.codTicket} draggableId={`p-${p.codTicket}`} index={idx}>
                        {(dragProvided)=>(
                          <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}>
                            <CardWrapper
                              p={p}
                              onOpenSeguimientos={(pp)=> openDrawerSeguimientos(pp)}
                              onOpenDetail={(pp)=> handleOpenDetail(pp)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}

                    <div ref={col.sentinelRef} style={{ height: 1 }} />
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Paper>
          ))}
        </Stack>
      </DragDropContext>

      {/* Drawer Seguimientos */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx:{ width:{ xs:"100%", sm:520 }, bgcolor:"#0b0f14", color:"#e5e9f0" } }}
      >
        <Box sx={{ p:2.5, height:"100%", display:"flex", flexDirection:"column", gap:2 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={900}>Seguimientos</Typography>
            <Typography variant="body2" sx={{ color:"#aab4c3" }}>
              Ticket {proyectoSel?.codTicket} — {proyectoSel?.nomTicket}
            </Typography>
          </Stack>
          <Divider sx={{ borderColor:"#2c3442" }}/>
          <Box sx={{ flex:1, overflowY:"auto" }}>
            {loadingTracing ? (
              <Box sx={{ px: 0.5 }}>
                {[...Array(3)].map((_, i)=>(
                  <Stack key={i} direction="row" spacing={1.5} sx={{ mb:1.5 }}>
                    <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor:"#1a2030" }}/>
                    <Box sx={{ flex:1 }}>
                      <Skeleton variant="text" height={18} width="40%" sx={{ bgcolor:"#1a2030" }}/>
                      <Skeleton variant="rectangular" height={48} sx={{ bgcolor:"#1a2030", borderRadius: 2 }}/>
                    </Box>
                  </Stack>
                ))}
              </Box>
            ) : (
              <List dense sx={{ display:"flex", flexDirection:"column", gap:1 }}>
                {Array.isArray(tracing) && tracing.length > 0 ? tracing.map((s) => {
                  const ini = String(s.nomUsuarioCreacion || "?").slice(0,2).toUpperCase();
                  return (
                    <ListItem key={s.codTicketSeguimiento} alignItems="flex-start" sx={{ p:0, alignItems:"flex-start" }}>
                      <Box sx={{ display:"flex", gap:1.25, width:"100%" }}>
                        <Box sx={{
                          minWidth: 36, height: 36, borderRadius: "50%",
                          bgcolor: "#141a24", border: "1px solid #2c3442",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#cfd8e3", fontWeight: 900, fontSize: 12
                        }} title={s.nomUsuarioCreacion}>
                          {ini}
                        </Box>
                        <Box sx={{ flex:1 }}>
                          <Stack direction="row" spacing={1} alignItems="baseline">
                            <Typography variant="body2" fontWeight={900} sx={{ color:"#e5e9f0" }}>
                              {s.nomUsuarioCreacion || "—"}
                            </Typography>
                            <Typography variant="caption" sx={{ color:"#9aa6b8" }}>
                              {s.fechaCreacion || "—"}
                            </Typography>
                          </Stack>
                          <Box sx={{
                            mt: .5, p: 1, bgcolor: "#0f141c",
                            border: "1px solid #2c3442", borderRadius: 2, color: "#dfe7ff", whiteSpace: "pre-wrap"
                          }}>
                            {s.descripcion || "—"}
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                  );
                }) : (
                  <Typography variant="body2" sx={{ color:"#aab4c3" }}>
                    Aún no hay seguimientos.
                  </Typography>
                )}
              </List>
            )}
          </Box>

          <Stack spacing={1}>
            <TextField
              label="Nuevo seguimiento"
              placeholder="Escribe un comentario…"
              multiline minRows={2}
              value={nuevoSeg}
              onChange={(e)=> setNuevoSeg(e.target.value)}
              sx={{
                input:{ color:"#e5e9f0" }, textarea:{ color:"#e5e9f0" },
                label:{ color:"#9aa6b8" }, "& fieldset":{ borderColor:"#3a4457" }
              }}
            />
            <Stack direction="row" gap={1} justifyContent="flex-end">
              <Button onClick={closeDrawer} sx={{ color:"#dfe7ff" }}>Cerrar</Button>
              <Button
                variant="contained"
                onClick={handleAddSeguimiento}
                disabled={savingSeg || !nuevoSeg.trim()}
                sx={{ bgcolor:"#6aa3ff", color:"#0b0f14", "&:hover":{ bgcolor:"#86b6ff" } }}
              >
                {savingSeg ? "Guardando…" : "Agregar"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>

      {/* Diálogo fecha */}
      <Dialog
        open={openDateDlg}
        onClose={()=> setOpenDateDlg(false)}
        PaperProps={{ sx:{ ...darkPaper, borderRadius: 3 } }}
      >
        <DialogTitle>Seleccionar fecha inicial</DialogTitle>
        <DialogContent dividers sx={{ borderColor:"#2c3442" }}>
          <TextField
            type="date"
            label="Desde"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={startDateISO}
            onChange={(e)=> setStartDateISO(e.target.value)}
            sx={{
              mt: 1,
              input:{ color:"#e5e9f0", caretColor:"#e5e9f0" },
              label:{ color:"#9aa6b8" }, "& fieldset":{ borderColor:"#3a4457" },
              "& input[type='date']::-webkit-calendar-picker-indicator": { filter: "invert(1) opacity(.9)", cursor: "pointer" }
            }}
          />
          <TextField
            label="Hasta (hoy)"
            fullWidth
            value={todayDDMM()}
            disabled
            sx={{ mt: 2, input:{ color:"#cfd8e3" }, label:{ color:"#9aa6b8" }, "& fieldset":{ borderColor:"#3a4457" } }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop:"1px solid #2c3442" }}>
          <Button onClick={()=> setOpenDateDlg(false)} sx={{ color:"#dfe7ff" }}>Cerrar</Button>
          <Button
            variant="contained"
            onClick={()=> { setOpenDateDlg(false); handleReload(); }}
            sx={{ bgcolor:"#6aa3ff", color:"#0b0f14", "&:hover":{ bgcolor:"#86b6ff" } }}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detalle */}
      <Suspense fallback={null}>
        <ProjectDetailDialog open={openDetail} onClose={handleCloseDetail} data={detailData} users={usuariosArea}/>
      </Suspense>

      {/* Cierre */}
      <Dialog
        open={closeDlgOpen}
        onClose={closing ? undefined : handleCancelClose}
        PaperProps={{ sx:{ width: 640, maxWidth:"95vw", ...darkPaper, borderRadius: 3 } }}
      >
        <DialogTitle>Cerrar proyecto</DialogTitle>
        <DialogContent dividers sx={{ borderColor:"#2c3442" }}>
          <Typography variant="body2" sx={{ color:"#9aa6b8", mb: 1 }}>
            Ticket {closingTicket?.codTicket} — {closingTicket?.nomTicket}
          </Typography>

          <TextField
            label="Motivo de cierre"
            placeholder="Describe brevemente la razón del cierre…"
            multiline minRows={3} fullWidth
            value={closeReason}
            onChange={(e)=> setCloseReason(e.target.value)}
            sx={{
              mt: 1,
              input:{ color:"#e5e9f0", caretColor:"#e5e9f0" }, textarea:{ color:"#e5e9f0" },
              label:{ color:"#9aa6b8" }, "& fieldset":{ borderColor:"#3a4457" }
            }}
          />

          <Typography variant="body2" sx={{ color:"#9aa6b8", mt: 2 }}>
            Evidencias (opcional): arrastra, pega una captura o haz click para seleccionar imágenes
          </Typography>

          <DropPasteZone files={closeFiles} setFiles={setCloseFiles} />
        </DialogContent>

        <DialogActions sx={{ borderTop:"1px solid #2c3442" }}>
          <Button onClick={handleCancelClose} disabled={closing} sx={{ color:"#dfe7ff" }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleConfirmClose}
            disabled={closing || !closeReason.trim()}
            sx={{ bgcolor:"#6aa3ff", color:"#0b0f14", "&:hover":{ bgcolor:"#86b6ff" } }}
          >
            {closing ? "Finalizando…" : "Finalizar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
